import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CLIENT_PATHS } from './helpers/constants/paths.core.constants';
import { AuthGuard } from './guards/auth.guard';
import { ApphomelayoutComponent } from './modules/core/layouts/apphomelayout/apphomelayout.component';
import { TenantFeatures } from './state/feature.repository';
import { UserRoles } from './state/auth.repository';
import { PagenotfoundComponent } from './modules/core/pages/pagenotfound/pagenotfound.component';
import { EnvironmentFeatureGuard } from './guards/environment.features.guard';
import {
  coreHomeRoutes,
  coreTenantRoutes,
} from './routing/core-routing.module';

const routes: Routes = [
  {
    path: '',
    component: ApphomelayoutComponent,
    canActivateChild: [AuthGuard],
    children: [
      ...coreHomeRoutes,
      ...coreTenantRoutes,
      {
        path: 'tenantIdDeepLink',
        loadChildren: () =>
          import('./modules/reports/reports.module').then(
            (m) => m.ReportsModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.reports,
        loadChildren: () =>
          import('./modules/reports/reports.module').then(
            (m) => m.ReportsModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.TenantAdmin],
        },
      },
      {
        path: CLIENT_PATHS.avatars,
        loadChildren: () =>
          import('./modules/avatars/avatars.module').then(
            (m) => m.AvatarsModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.Superadmin],
        },
      },
      {
        path: CLIENT_PATHS.tags,
        loadChildren: () =>
          import('./modules/tags/tags.module').then(
            (m) => m.TagsModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.Superadmin],
        },
      },
      {
        path: CLIENT_PATHS.roleplayLibrary,
        loadChildren: () =>
          import('./modules/roleplay-library/roleplay-library.module').then(
            (m) => m.RoleplayLibraryModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.Superadmin],
        },
      },
      {
        path: CLIENT_PATHS.personalPlan,
        loadChildren: () =>
          import('./modules/my-personal-plan/personalplan.module').then(
            (m) => m.PersonalPlanModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.PersonalPlan,
        },
      },
      {
        path: CLIENT_PATHS.personalCoach,
        loadChildren: () =>
          import('./modules/personalCoach/personalCoachModule').then(
            (m) => m.PersonalCoachModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.PersonalPlan,
        },
      },
      {
        path: CLIENT_PATHS.deleting,
        loadChildren: () =>
          import('./modules/permanentlyDelete/permanently-delete.module').then(
            (m) => m.PermanentlyDeleteModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [],
        },
      },
      {
        path: CLIENT_PATHS.emails,
        loadChildren: () =>
          import('./modules/emails/emails.module').then((m) => m.EmailsModule),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.usergroups,
        loadChildren: () =>
          import('./modules/usergroup/usergroup.module').then(
            (m) => m.UsergroupModule,
          ),
        canActivate: [AuthGuard],
        data: {},
      },
      {
        path: CLIENT_PATHS.sprints,
        loadChildren: () =>
          import('./modules/sprints/sprints.module').then(
            (m) => m.SprintsModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: [TenantFeatures.Sprints, TenantFeatures.Onboarding],
        },
      },
      {
        path: CLIENT_PATHS.calendar,
        loadChildren: () =>
          import('./modules/calendar/calendar.module').then(
            (m) => m.CalendarModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Calendar,
        },
      },
      {
        path: CLIENT_PATHS.feed,
        loadChildren: () =>
          import('./modules/feed/feed.module').then((m) => m.FeedModule),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Feed,
        },
      },
      {
        path: CLIENT_PATHS.CRM,
        loadChildren: () =>
          import('./modules/crm/crm.module').then((m) => m.CrmModule),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.CRM,
        },
      },
      {
        path: CLIENT_PATHS.onboarding,
        loadChildren: () =>
          import('./modules/onboarding/onboarding.module').then(
            (m) => m.OnboardingModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: [TenantFeatures.Onboarding, TenantFeatures.Sprints],
        },
      },
      {
        path: CLIENT_PATHS.toolbox,
        loadChildren: () =>
          import('./modules/toolbox/toolbox.module').then(
            (m) => m.ToolboxModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.ToolBox,
        },
      },
      {
        path: CLIENT_PATHS.mediaLibrary,
        loadChildren: () =>
          import('./modules/mediaLibrary/media-library.module').then(
            (m) => m.MediaLibraryModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.ToolBox,
        },
      },
      {
        path: CLIENT_PATHS.posts,
        loadChildren: () =>
          import('./modules/news/news.module').then((m) => m.NewsModule),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.News,
        },
      },
      {
        path: CLIENT_PATHS.aiRoleplay,
        loadChildren: () =>
          import('./modules/airoleplay/airoleplay.module').then(
            (m) => m.AIRoleplayModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.AIRoleplay,
        },
      },
      // },
      {
        path: CLIENT_PATHS.playBook,
        loadChildren: () =>
          import('./modules/playBook/playbook.module').then(
            (m) => m.PlaybookModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.PlayBook,
        },
      },
      {
        path: CLIENT_PATHS.playBookPilot,
        loadChildren: () =>
          import('./modules/playBook/playbook.module').then(
            (m) => m.PlaybookModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.PlayBookPilot,
        },
      },
      {
        path: CLIENT_PATHS.poll,
        loadChildren: () =>
          import('./modules/poll/poll.module').then((m) => m.PollModule),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Poll,
        },
      },
      {
        path: CLIENT_PATHS.todo,
        loadChildren: () =>
          import('./modules/todo/todo.module').then((m) => m.TodoModule),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Todo,
        },
      },
      {
        path: CLIENT_PATHS.gamification,
        loadChildren: () =>
          import('./modules/gamification/gamification.module').then(
            (m) => m.GamificationModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Gamification,
        },
      },
      {
        path: CLIENT_PATHS.billing,
        loadChildren: () =>
          import('./modules/billing/billing.module').then(
            (m) => m.BillingModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Billing,
        },
      },
      {
        path: CLIENT_PATHS.dashboard,
        loadChildren: () =>
          import('./modules/dashboard/dashboard.module').then(
            (m) => m.DashboardModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.Superadmin],
        },
      },

      {
        path: CLIENT_PATHS.employee,
        loadChildren: () =>
          import('./modules/employee/employee.module').then(
            (m) => m.EmployeeModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Employee,
        },
      },
      {
        path: CLIENT_PATHS.dataRoom,
        loadChildren: () =>
          import('./modules/dataRoom/dataRoom.module').then(
            (m) => m.DataRoomModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.chat,
        loadChildren: () =>
          import('./modules/coachmenteechat/coachmenteechat.module').then(
            (m) => m.CoachMenteeChatModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.activities,
        loadChildren: () =>
          import('./modules/activities/activities.module').then(
            (m) => m.ActivitiesModule,
          ),
        canActivate: [AuthGuard],
        data: {
          authorize: [UserRoles.Superadmin],
        },
      },
      {
        path: CLIENT_PATHS.product,
        loadChildren: () =>
          import('./modules/product/product.module').then(
            (m) => m.ProductModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.Product,
        },
      },
      {
        path: CLIENT_PATHS.kemihotelletapv,
        loadChildren: () =>
          import('./modules/kemihotelletapv/kemihotelletapv.module').then(
            (m) => m.KemiHotelletAPVModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.KemiHotelletAPV,
        },
      },
      {
        // Legacy route — preserved so existing deep links keep
        // working. The canonical URL now lives at
        // /ai-assistant/pilot under the new AI Assistant group.
        path: CLIENT_PATHS.aiAdvancedPilot,
        redirectTo: CLIENT_PATHS.aiAssistant + '/pilot',
        pathMatch: 'full',
      },
      {
        // New "AI Assistant" grouping. Owns the pilot page plus the
        // user-owned Pilot Accounts / Contacts for tenants that don't
        // use the full CRM.
        path: CLIENT_PATHS.aiAssistant,
        loadChildren: () =>
          import('./modules/ai-assistant/ai-assistant.module').then(
            (m) => m.AiAssistantModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.meetingRoom,
        loadChildren: () =>
          import('./modules/meeting-room/meeting-room.module').then(
            (m) => m.MeetingRoomModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: CLIENT_PATHS.programBuilder,
        loadChildren: () =>
          import('./modules/program-builder/program-builder.module').then(
            (m) => m.ProgramBuilderModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.ProgramBuilder,
        },
      },
       {
        path: CLIENT_PATHS.myTeam,
        loadChildren: () =>
          import('./modules/my-team/my-team.module').then(
            (m) => m.MyTeamModule,
          ),
        canActivate: [AuthGuard],
        data: {
          feature: TenantFeatures.MyTeam,
        },
      },
    ],
  },
  { path: '**', component: PagenotfoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'disabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
